import React, { useCallback, useMemo } from 'react';

import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';

import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

import { getIdFromAri, getScorecardsSortedByHealth } from '../../../common/utils';
import { useGetComponentScorecards } from '../../../services/use-get-component-scorecards';
import { ScorecardGlyph } from './assets';
import { GenericErrorGlyph } from './generic-error-glyph';
import LoadingSpinner from './loading-spinner';
import messages from './messages';
import ProgressBar from './progress-bar';

const NUMBER_OF_SCORECARDS_TO_DISPLAY = 5;

type Props = {
	testId?: string;
	componentId: string;
};

export default function ScorecardsHealthInfoDetails({ testId, componentId }: Props) {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { scorecards, loading, error } = useGetComponentScorecards(componentId);

	const scorecardsSortedByHealth = useMemo(
		() => getScorecardsSortedByHealth(scorecards),
		[scorecards],
	);

	const handleViewMoreClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({}),
			'button clicked',
			'scorecardsHealthInfoDetailsViewMore',
		);
	}, [createAnalyticsEvent]);

	return (
		<ContentWrapper data-testid={testId}>
			{loading && <LoadingSpinner />}
			{!loading && error && (
				<ErrorWrapper>
					<GenericErrorGlyph data-testid={`${testId}.generic-error-glyph`} />
				</ErrorWrapper>
			)}
			{!loading &&
				!error &&
				scorecardsSortedByHealth.slice(0, NUMBER_OF_SCORECARDS_TO_DISPLAY).map((scorecard, i) => {
					const totalScore = scorecard?.scorecardScore?.totalScore ?? 0;
					const { id: scorecardId } = scorecard;
					return (
						<React.Fragment key={scorecardId}>
							<ScorecardWrapper data-testid={`${testId}-scorecard-${i}`}>
								<ScorecardInfoWrapper>
									<ScorecardName>
										<ScorecardButtonWrapper>
											<Button
												appearance="subtle-link"
												spacing="none"
												onClick={(_, analyticsEvent) => {
													fireUIAnalytics(
														analyticsEvent,
														'scorecardsHealthInfoDetailsScorecardName',
													);
												}}
												href={`/compass/scorecard/${getIdFromAri(scorecardId)}`}
												target="_blank"
												rel="noopener noreferrer"
												iconBefore={
													<Box xcss={iconWrapperStyles}>
														<ScorecardGlyph />
													</Box>
												}
											>
												{scorecard.name}
											</Button>
										</ScorecardButtonWrapper>
									</ScorecardName>
									<ScorecardScore>{totalScore}%</ScorecardScore>
								</ScorecardInfoWrapper>
								<ProgressBar
									progress={totalScore}
									size="medium"
									testId={`dragonfruit-scorecards.common.ui.progress-bar-${scorecardId}`}
								/>
							</ScorecardWrapper>
						</React.Fragment>
					);
				})}

			{!error && scorecards.length > NUMBER_OF_SCORECARDS_TO_DISPLAY && (
				<LinkWrapper
					href={`/compass/component/${getIdFromAri(componentId)}`}
					target="_blank"
					rel="noopener noreferrer"
					onClick={handleViewMoreClick}
				>
					{formatMessage(messages.viewMore)}
				</LinkWrapper>
			)}
		</ContentWrapper>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	width: 264,
	minHeight: 30,
	padding: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScorecardWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:not(:last-of-type)': {
		marginBottom: token('space.200', '16px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScorecardInfoWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',

	marginBottom: token('space.150', '12px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScorecardName = styled.div({
	overflow: 'hidden',
	textOverflow: 'ellipsis',

	display: '-webkit-box',
	'-webkit-line-clamp': 1,
	'-webkit-box-orient': 'vertical',
});

const iconWrapperStyles = xcss({
	paddingRight: 'space.075',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScorecardButtonWrapper = styled.div({
	// to remove the nested buttons padding for alignment
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: `${token('color.text', colors.N800)} !important`,
		paddingLeft: token('space.025', '2px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScorecardScore = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
	paddingTop: token('space.025', '2px'),
	fontSize: '14px',
	fontWeight: 600,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkWrapper = styled.a({
	display: 'block',
	marginTop: token('space.200', '16px'),
	fontWeight: 500,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: `${token('color.text', colors.N800)}`,
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: `${token('color.text', colors.N800)}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});
